import {
  DateRange,
  dateRangeOverlapsDate,
  DateString,
  DayOfWeek,
  firstDayOfWeek,
  lastDayOfWeek,
  lastSundayDate,
  nextSundayDate,
  thisSundayDate,
} from './date_string.ts';
import {
  HymnOfTheMonthSongList,
  IntervalSongList,
  Org,
  OrgBase,
  OrgType,
  SongList,
  SongListEntry,
  SongListEntryType,
  SongListType,
} from './model.ts';
import { getHymnsOfTheMonthForDate, getWeeklySongEntriesForDate } from './repeating_rows.ts';

export const todayTitle = 'Today';
export const thisSundayTitle = 'This Sunday';
const lastSundayTitle = 'Last Sunday';
const nextSundayTitle = 'Next Sunday';

export const staffCanEditEvents = true;

type TitledSong = { psalm?: string; title?: string };

// a song counts as a psalm when it carries a psalm number or its title names one
function isPsalm(song: TitledSong): boolean {
  return !!song.psalm || !!song.title?.toLowerCase().includes('psalm');
}

export function getHymnOfTheMonthOrTermTitle(hymns: TitledSong[], intervalName: string, name?: string): string {
  if (name) {
    return name;
  }
  const allPsalms = hymns.length > 0 && hymns.every(isPsalm);
  const prefix = allPsalms ? 'Psalm' : 'Hymn';
  const pluralEnding = hymns.length > 1 ? 's' : '';
  return `${prefix}${pluralEnding} of the ${intervalName}`;
}

// The heading of the Church tab's one section for the month's songs (issue #4193): as a school's heading for one song
// or for several that are all psalms or all hymns, and 'Songs of the Month' for several that mix the two. Schools keep
// getHymnOfTheMonthOrTermTitle.
export function getSongsOfTheMonthTitle(hymns: TitledSong[]): string {
  const psalmCount = hymns.filter(isPsalm).length;
  if (hymns.length > 1 && psalmCount > 0 && psalmCount < hymns.length) {
    return 'Songs of the Month';
  }
  return getHymnOfTheMonthOrTermTitle(hymns, 'Month');
}

// The Church tab shows every Hymn of the Month list as one section (issue #4193): one list that keeps the first
// list's fields and holds every list's songs in the order the lists come. It is for display only and never saved; its
// entries keep their own row numbers, which may repeat. getHymnsOfTheMonthForDate already shows a song held by two
// lists once, so nothing is deduplicated here.
export function combineHymnsOfTheMonth(hymnsOfTheMonth: HymnOfTheMonthSongList[]): HymnOfTheMonthSongList[] {
  const [first, ...rest] = hymnsOfTheMonth;
  if (!first || rest.length === 0) {
    return hymnsOfTheMonth;
  }
  return [{ ...first, songs: hymnsOfTheMonth.flatMap(({ songs }) => songs) }];
}

export function getSundayTitle(date: DateString, today: DateString) {
  switch (date) {
    case today:
      return todayTitle;
    case thisSundayDate(today):
      return thisSundayTitle;
    case lastSundayDate(today):
      return lastSundayTitle;
    case nextSundayDate(today):
      return nextSundayTitle;
    default:
      return date;
  }
}

export function getWeeklyDateForOrganization(org: OrgBase | undefined, date: DateString): DateString {
  return org?.type === OrgType.Church ? thisSundayDate(date) : firstDayOfWeek(date);
}

// from the first day of the week of the term start to the last day of the week of the term end
export function getSchoolTermDateRange(schoolTerms: IntervalSongList[], date: DateString): DateRange | undefined {
  for (const { interval } of schoolTerms) {
    if (dateRangeOverlapsDate(interval, date)) {
      return { start: firstDayOfWeek(interval.start), end: lastDayOfWeek(interval.end) };
    }
  }
}

// returns a custom interval song list for the specified date (for printing and copying songs on a specific date)
export function getSongEntriesForDate(
  org: Org,
  date: DateString,
  songLists: SongList[],
  entryTypes: SongListEntryType[],
  {
    forSingingPlan,
    forPdfPacket,
    today,
    writtenThrough,
  }: {
    forSingingPlan?: boolean;
    forPdfPacket?: boolean;
    today?: DateString;
    // the last Sunday the server has written the church down through, for the weekly songs' fill (FillClock)
    writtenThrough?: DateString;
  } = {},
): (SongListEntry & { category?: string })[] {
  const songs: SongListEntry[] = [];
  if (org?.type === OrgType.School) {
    const rows = (org.churchLiturgySchema?.rows ?? []).filter(({ type }) => entryTypes.includes(type));
    for (const { row, intervalType, assigned, label } of rows) {
      if (forSingingPlan && !assigned) {
        continue;
      }
      for (const songList of songLists) {
        if (songList.type === SongListType.Interval && songList.interval.type === intervalType) {
          songs.push(
            ...(songList.songs
              .filter(({ row: currentRow }) => currentRow === row)
              .map((entry) => (forSingingPlan ? { ...entry, category: label } : entry)) ?? []),
          );
        }
      }
    }
  } else {
    if (!forPdfPacket && entryTypes.includes(SongListEntryType.Song)) {
      for (const hymnOfTheMonth of getHymnsOfTheMonthForDate(org, date, songLists, { today })) {
        if (hymnOfTheMonth.songs.length === 1) {
          songs.push(hymnOfTheMonth.songs[0]);
        }
      }
    }
    songs.push(...getWeeklySongEntriesForDate(org, date, songLists, { today, writtenThrough }));
  }
  return songs;
}

export function isSingingPlanForDate(orgs: Org[], forDate: DateString): boolean {
  return !!orgs.find(({ singingPlan }) => singingPlan?.days.find(({ date }) => date === forDate));
}

export const schoolSingingPlanSkipDays = [DayOfWeek.Saturday, DayOfWeek.Sunday];
export const churchSingingPlanSkipDays = [DayOfWeek.Sunday];
export const singingPlanCompletionDurationMs = 30000;
